/**
 * MemberFormModal Component
 * Modal for adding and editing members with profile picture upload
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import DatePicker from 'react-datepicker';
import { format, parse, addMonths } from 'date-fns';
import 'react-datepicker/dist/react-datepicker.css';
import { Modal } from '../ui/Modal';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { SearchableSelect } from '../ui/SearchableSelect';
import { Button } from '../ui/Button';
import { membersService, type MemberFormData } from '../../services/members.service';
import { teamsService } from '../../services/teams.service';
import { useToast } from '../../hooks/useToast';
import { useMemberImage } from '../../hooks/useMemberImage';
import {
  validateEmail,
  checkEmailExists,
  getSuggestedEmail,
  type EmailValidationResult,
} from '../../lib/emailValidation';

export interface MemberFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'add' | 'edit';
  memberId?: number;
}

export const MemberFormModal = ({ isOpen, onClose, mode, memberId }: MemberFormModalProps) => {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<Partial<MemberFormData>>({
    first_name: '',
    last_name: '',
    email: '',
    gender: 'male',
    date_of_birth: '',
    position: '',
    team_id: 0,
    date_joined: '',
    biography: '',
  });

  const [profilePicture, setProfilePicture] = useState<File | null>(null);

  // Email validation state
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailWarning, setEmailWarning] = useState<string | null>(null);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const emailCheckTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const originalEmailRef = useRef<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showTeamTooltip, setShowTeamTooltip] = useState(false);

  // Fetch teams for dropdown
  const { data: teams = [] } = useQuery({
    queryKey: ['teams'],
    queryFn: teamsService.getAll,
  });

  // Fetch member data for edit mode
  const { data: member } = useQuery({
    queryKey: ['member', String(memberId)],
    queryFn: () => membersService.getById(memberId!),
    enabled: mode === 'edit' && !!memberId,
  });

  // Load member image with automatic retry on blob URL failures
  // Only load when modal is open, in edit mode, and member data is available
  const { imageUrl: currentImageUrl, handleImageError } = useMemberImage(
    isOpen && mode === 'edit' && member ? member.id : null,
    member?.has_profile_picture ?? false
  );

  // Populate form when member data loads or modal opens
  useEffect(() => {
    if (isOpen && mode === 'edit' && member) {
      setFormData({
        first_name: member.first_name,
        last_name: member.last_name,
        email: member.email,
        gender: member.gender,
        date_of_birth: member.date_of_birth ?? undefined,
        position: member.position ?? undefined,
        team_id: member.team_id ?? undefined,
        date_joined: member.date_joined,
        biography: member.biography || '',
      });
      // Track original email for edit mode (to skip validation if unchanged)
      originalEmailRef.current = member.email;
    }
  }, [isOpen, mode, member]);

  // Reset form when switching to add mode
  useEffect(() => {
    if (mode === 'add') {
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        gender: 'male',
        date_of_birth: '',
        position: '',
        team_id: 0,
        date_joined: '',
        biography: '',
      });
      setProfilePicture(null);
    }
  }, [mode]);

  // Reset form when modal closes or mode changes
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        gender: 'male',
        date_of_birth: '',
        position: '',
        team_id: 0,
        date_joined: '',
        biography: '',
      });
      setProfilePicture(null);
      setEmailError(null);
      setEmailWarning(null);
      setShowTeamTooltip(false);
      originalEmailRef.current = null;
      // Clear any pending email check
      if (emailCheckTimeoutRef.current) {
        clearTimeout(emailCheckTimeoutRef.current);
      }
    }
  }, [isOpen, mode, memberId]);

  // Email validation handler (called on blur)
  const handleEmailBlur = useCallback(async () => {
    const email = formData.email?.trim() || '';

    // Skip validation if email is unchanged in edit mode
    if (mode === 'edit' && email.toLowerCase() === originalEmailRef.current?.toLowerCase()) {
      setEmailError(null);
      setEmailWarning(null);
      return;
    }

    // First, validate format
    const formatResult: EmailValidationResult = validateEmail(email);

    if (!formatResult.isValid) {
      setEmailError(formatResult.error);
      setEmailWarning(null);
      return;
    }

    // Set warning if present (TLD typo)
    setEmailWarning(formatResult.warning);

    // Clear any pending timeout
    if (emailCheckTimeoutRef.current) {
      clearTimeout(emailCheckTimeoutRef.current);
    }

    // Debounce the duplicate check (500ms)
    setIsCheckingEmail(true);
    emailCheckTimeoutRef.current = setTimeout(async () => {
      try {
        const duplicateResult = await checkEmailExists(
          email,
          mode === 'edit' ? memberId : undefined
        );

        if (duplicateResult.exists) {
          setEmailError(duplicateResult.error);
        } else {
          setEmailError(null);
        }
      } catch {
        // If check fails, clear error (backend will catch it)
        setEmailError(null);
      } finally {
        setIsCheckingEmail(false);
      }
    }, 500);
  }, [formData.email, mode, memberId]);

  // Handle email change - clear errors as user types
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setFormData((prev) => ({ ...prev, email: value }));
    // Clear error when user starts typing again
    if (emailError) {
      setEmailError(null);
    }
  };

  // Apply suggested TLD correction
  const applySuggestedEmail = () => {
    const suggested = getSuggestedEmail(formData.email || '');
    if (suggested) {
      setFormData((prev) => ({ ...prev, email: suggested }));
      setEmailWarning(null);
    }
  };

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: MemberFormData) => {
      console.log('Creating member with data:', data);
      return membersService.create(data);
    },
    onSuccess: async () => {
      console.log('Member created successfully');
      toast.success('Member added successfully');
      // Refetch queries to ensure list updates (including dashboard if opened from there)
      await queryClient.refetchQueries({ queryKey: ['members'] });
      await queryClient.refetchQueries({ queryKey: ['dashboardOverview'] });
      await queryClient.refetchQueries({ queryKey: ['dashboardStats'] });
      onClose();
    },
    onError: (error: unknown) => {
      console.error('Failed to create member:', error);
      const message = error instanceof Error ? error.message : 'Failed to add member';
      toast.error(message);
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: MemberFormData) => {
      console.log('Updating member with data:', data);
      return membersService.update(memberId!, data);
    },
    onSuccess: async () => {
      toast.success('Member updated successfully');

      // Clear the image cache for this member if profile picture was updated
      if (profilePicture) {
        membersService.revokeMemberImage(memberId!);
      }

      // Refetch queries to ensure list and detail page update (including dashboard if opened from there)
      await queryClient.refetchQueries({ queryKey: ['members'] });
      await queryClient.refetchQueries({ queryKey: ['member', String(memberId)] });
      await queryClient.refetchQueries({ queryKey: ['dashboardOverview'] });
      await queryClient.refetchQueries({ queryKey: ['dashboardStats'] });
      onClose();
    },
    onError: (error: unknown) => {
      console.error('Failed to update member:', error);
      const message = error instanceof Error ? error.message : 'Failed to update member';
      toast.error(message);
    },
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Date picker handlers
  const handleDateChange = (field: 'date_of_birth' | 'date_joined') => (date: Date | null) => {
    setFormData((prev) => ({
      ...prev,
      [field]: date ? format(date, 'yyyy-MM-dd') : '',
    }));
  };

  const parseISODate = (dateString: string | null | undefined): Date | null => {
    if (!dateString) return null;
    try {
      return parse(dateString, 'yyyy-MM-dd', new Date());
    } catch {
      return null;
    }
  };

  // Handle backspace to skip over separators (must use onKeyDown to intercept BEFORE change)
  const handleDateKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Backspace') {
      const target = e.target as HTMLInputElement;
      const cursorPos = target.selectionStart || 0;

      // If cursor is right after a separator (position 3 or 6), skip it
      if (cursorPos === 3 || cursorPos === 6) {
        e.preventDefault();
        // Remove the digit before the separator
        const value = target.value;
        const newValue = value.slice(0, cursorPos - 2) + value.slice(cursorPos - 1);
        // Reformat
        const digits = newValue.replace(/\D/g, '');
        let formatted = '';
        if (digits.length >= 2) {
          formatted = digits.slice(0, 2) + '/';
          if (digits.length >= 4) {
            formatted += digits.slice(2, 4) + '/';
            formatted += digits.slice(4, 8);
          } else {
            formatted += digits.slice(2);
          }
        } else {
          formatted = digits;
        }
        target.value = formatted;
        // Set cursor position
        const newCursorPos = cursorPos - 2;
        target.setSelectionRange(newCursorPos, newCursorPos);
      }
    }
  };

  // Auto-add separators when typing date (forward typing only)
  const handleDateInputRaw = (e: React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement> | undefined) => {
    if (!e) return;
    const target = e.target as HTMLInputElement;
    if (!target || target.tagName !== 'INPUT') return;

    // Auto-add separators when typing
    const value = target.value.replace(/\D/g, '');
    let formatted = '';

    if (value.length >= 2) {
      formatted = value.slice(0, 2) + '/';
      if (value.length >= 4) {
        formatted += value.slice(2, 4) + '/';
        formatted += value.slice(4, 8);
      } else {
        formatted += value.slice(2);
      }
    } else {
      formatted = value;
    }

    target.value = formatted;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        e.target.value = '';
        return;
      }

      // Validate file type
      if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
        toast.error('File must be JPG or PNG');
        e.target.value = '';
        return;
      }

      setProfilePicture(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    console.log('Form submission started. Form data:', formData);

    // Validation - check all required fields
    if (!formData.first_name || !formData.last_name || !formData.email ||
        !formData.date_joined) {
      console.error('Validation failed. Missing fields:', {
        first_name: !formData.first_name,
        last_name: !formData.last_name,
        email: !formData.email,
        date_joined: !formData.date_joined,
      });
      toast.error('Please fill in all required fields');
      return;
    }

    // Check for email validation errors (skip if unchanged in edit mode)
    const emailUnchanged = mode === 'edit' &&
      formData.email.trim().toLowerCase() === originalEmailRef.current?.toLowerCase();

    if (!emailUnchanged) {
      // Run final email validation
      const emailResult = validateEmail(formData.email);
      if (!emailResult.isValid) {
        setEmailError(emailResult.error);
        toast.error(emailResult.error || 'Invalid email');
        return;
      }
    }

    // Block submission if there's a validation error
    if (emailError) {
      toast.error(emailError);
      return;
    }

    const submitData: MemberFormData = {
      first_name: formData.first_name!,
      last_name: formData.last_name!,
      email: formData.email!,
      gender: formData.gender as 'male' | 'female',
      date_of_birth: formData.date_of_birth || null,
      position: formData.position || null,
      team_id: formData.team_id && formData.team_id !== 0 ? Number(formData.team_id) : null,
      date_joined: formData.date_joined!,
      biography: formData.biography,
    };

    if (profilePicture) {
      submitData.profile_picture = profilePicture;
    }

    if (mode === 'edit') {
      updateMutation.mutate(submitData);
    } else {
      createMutation.mutate(submitData);
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={mode === 'edit' ? 'Edit Member' : 'Add Member'}>
      <form onSubmit={handleSubmit} className="space-y-4" onClick={() => setShowTeamTooltip(false)}>
        {/* First and Last Name */}
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="First Name"
            name="first_name"
            value={formData.first_name || ''}
            onChange={handleChange}
            required
          />
          <Input
            label="Last Name"
            name="last_name"
            value={formData.last_name || ''}
            onChange={handleChange}
            required
          />
        </div>

        {/* Email */}
        <div>
          <Input
            label="Email"
            type="email"
            name="email"
            value={formData.email || ''}
            onChange={handleEmailChange}
            onBlur={handleEmailBlur}
            required
            error={emailError || undefined}
            helperText={isCheckingEmail ? 'Checking email...' : undefined}
          />
          {emailWarning && !emailError && !isCheckingEmail && (
            <p className="mt-1 text-sm text-blue-600 dark:text-blue-400 flex items-center gap-2">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <span>{emailWarning}</span>
              <button
                type="button"
                onClick={applySuggestedEmail}
                className="underline hover:no-underline font-medium"
              >
                Apply
              </button>
            </p>
          )}
        </div>

        {/* Gender and Date of Birth */}
        <div className="grid grid-cols-2 gap-4">
          <Select
            label="Gender"
            required
            name="gender"
            value={formData.gender || 'male'}
            onChange={handleChange}
            options={[
              { value: 'male', label: 'Male' },
              { value: 'female', label: 'Female' },
            ]}
          />
          <div className="w-full">
            <label className="block text-sm font-medium text-body mb-1">
              Date of Birth
            </label>
            <DatePicker
              selected={parseISODate(formData.date_of_birth)}
              onChange={handleDateChange('date_of_birth')}
              onKeyDown={handleDateKeyDown}
              onChangeRaw={handleDateInputRaw}
              dateFormat="dd/MM/yyyy"
              placeholderText="dd/mm/yyyy"
              isClearable
              showYearDropdown
              showMonthDropdown
              dropdownMode="scroll"
              yearDropdownItemNumber={100}
              scrollableYearDropdown
              maxDate={new Date()}
              className="block w-full rounded-md border-strong shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2 border text-heading bg-surface"
              wrapperClassName="w-full"
            />
          </div>
        </div>

        {/* Job Title (members.position, TW-401) */}
        <Input
          label="Job Title"
          name="position"
          value={formData.position || ''}
          onChange={handleChange}
          placeholder="e.g., Creative Director"
        />

        {/* Team and Date Joined */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <label className="block text-sm font-medium text-body">Team</label>
              <div className="relative">
                <button
                  type="button"
                  className="w-3 h-3 rounded-full bg-gray-400 dark:bg-gray-500 text-white text-[10px] font-bold leading-none flex items-center justify-center hover:bg-gray-500 dark:hover:bg-gray-400 focus:outline-none"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowTeamTooltip(!showTeamTooltip);
                  }}
                >
                  i
                </button>
                {showTeamTooltip && (
                  <div className="absolute left-0 top-6 z-20 w-64 p-3 text-sm text-body bg-surface border border-[var(--border-default)] rounded-md shadow-lg">
                    Team not listed? You can create it on the Teams page.
                    <br />
                    You can add/edit this member now and assign them to a team later.
                  </div>
                )}
              </div>
            </div>
            <SearchableSelect
              value={String(formData.team_id || '')}
              onChange={(value) => setFormData((prev) => ({ ...prev, team_id: value ? Number(value) : 0 }))}
              options={[
                { value: '', label: 'No Team Assigned' },
                ...teams.map((team) => ({
                  value: String(team.id),
                  label: team.name,
                })),
              ]}
              placeholder="Search teams..."
            />
          </div>
          <div className="w-full">
            <label className="block text-sm font-medium text-body mb-1">
              Date Joined Organization
              <span className="text-red-500 ml-1">*</span>
            </label>
            <DatePicker
              selected={parseISODate(formData.date_joined)}
              onChange={handleDateChange('date_joined')}
              onKeyDown={handleDateKeyDown}
              onChangeRaw={handleDateInputRaw}
              dateFormat="dd/MM/yyyy"
              placeholderText="dd/mm/yyyy"
              showYearDropdown
              showMonthDropdown
              dropdownMode="scroll"
              yearDropdownItemNumber={50}
              scrollableYearDropdown
              maxDate={addMonths(new Date(), 1)}
              className="block w-full rounded-md border-strong shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2 border text-heading bg-surface"
              wrapperClassName="w-full"
            />
          </div>
        </div>

        {/* Biography */}
        <div>
          <label htmlFor="biography" className="block text-sm font-medium text-body mb-1">
            Biography
          </label>
          <textarea
            id="biography"
            name="biography"
            rows={3}
            value={formData.biography || ''}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-strong rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-heading bg-surface"
            placeholder="Brief description about the member..."
          />
        </div>

        {/* Profile Picture */}
        <div>
          <label className="block text-sm font-medium text-body mb-1">
            Profile Picture
          </label>
          <div className="mt-1 flex items-center space-x-4">
            {/* Show current image if editing */}
            {mode === 'edit' && currentImageUrl && (
              <div className="flex-shrink-0">
                <img
                  src={currentImageUrl}
                  alt="Current"
                  className="w-16 h-16 rounded-full object-cover border-2 border-[var(--border-default)]"
                  onError={handleImageError}
                />
              </div>
            )}
            <div className="flex-1">
              <input
                type="file"
                id="profile_picture"
                name="profile_picture"
                ref={fileInputRef}
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="py-2 px-4 rounded-md text-sm font-semibold bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/30"
                >
                  Choose File
                </button>
                <span className="text-sm text-meta">
                  {profilePicture ? profilePicture.name : 'No file chosen'}
                </span>
              </div>
              <p className="text-xs text-meta mt-1">
                JPG or PNG, max 5MB. Image will be cropped to 160x160px.
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 pt-4">
          <Button type="button" variant="secondary" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button type="submit" isLoading={isLoading}>
            {mode === 'edit' ? 'Update Member' : 'Add Member'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
